import { useCallback, useEffect, useState } from 'react';
import type { ResultEntry } from '@filmsloth/shared';
import { api } from '../api/client.js';

interface ResultsState {
  results: ResultEntry[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Fetches the ranking while it is on screen. Results are derived data, so they
 * are pulled on demand instead of being pushed with every vote.
 */
export function useResults(token: string | null, active: boolean, version: number): ResultsState {
  const [results, setResults] = useState<ResultEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!token || !active) return;
    let cancelled = false;
    setLoading(true);
    api
      .results(token)
      .then((payload) => {
        if (cancelled) return;
        setResults(payload.results);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Request failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, active, version, nonce]);

  return { results, loading, error, reload };
}
